import { Injectable, computed, inject, signal } from '@angular/core';
import { dsDtos, webDtos } from '@vectronic/openApi-admin';
import { CameraLifetime, utcMoment } from '@vectronic/util';
import { Moment } from 'moment';
import { ScheduleTypeConfig } from './abstract-schedule.service';
import { CameraScheduleService } from './camera-schedule.service';
import { ScheduleCalendarCalculationService } from './schedule-calendar-calculation.service';

@Injectable({ providedIn: 'root' })
export class CameraLifetimeService {
  cameraScheduleService = inject(CameraScheduleService);
  private calendarCalculationService = inject(ScheduleCalendarCalculationService);

  // Configurable camera battery capacity in Ah (default 20 Ah)
  private cameraBatteryCapacityAhSig = signal<number>(20);
  setCameraBatteryCapacityAh(valueAh?: number) {
    const v = typeof valueAh === 'number' && isFinite(valueAh) && valueAh > 0 ? valueAh : 20;
    this.cameraBatteryCapacityAhSig.set(v);
  }
  cameraBatteryCapacityAh = computed(() => this.cameraBatteryCapacityAhSig());

  // Camera formats provided by AirtimeCalculatorService (if available)
  private cameraFormatsSig = signal<dsDtos.CameraFormat[] | undefined>(undefined);
  setCameraFormats(formats?: dsDtos.CameraFormat[]) {
    this.cameraFormatsSig.set(formats ?? []);
  }

  // Resolve video bitrate in bits/s based on selected camera format (dataRateHQ kBits/s)
  resolveVideoBitrateBps(rule: dsDtos.CameraScheduleRule): number | undefined {
    const id = rule?.cameraFormatId;
    const fmts = this.cameraFormatsSig();
    if (!id || !fmts || !fmts.length) return 15000000; // fallback 15 Mbps
    const fmt = fmts.find((f) => f?.formatId === id);
    const kbitsPerSec = fmt?.dataRateHQ; // kBits/s
    const bps =
      typeof kbitsPerSec === 'number' && isFinite(kbitsPerSec) && kbitsPerSec > 0
        ? kbitsPerSec * 1000 // convert kBits/s to bits/s
        : undefined;
    return bps ?? 15000000;
  }

  // Resolve per-photo storage in bytes based on selected camera format
  resolvePhotoStorageBytes(rule: dsDtos.CameraScheduleRule): number {
    const id = rule?.cameraFormatId;
    const fmts = this.cameraFormatsSig();
    const fallback = 6784;
    if (!id || !fmts || !fmts.length) return fallback;
    const fmt = fmts.find((f) => f?.formatId === id);
    const bytes = fmt?.dataStorage;
    const valid = typeof bytes === 'number' && isFinite(bytes) && bytes > 0 ? bytes : undefined;
    return valid ?? fallback;
  }

  // Resolve average current draw (mA) per video based on selected camera format
  // current_mA = currentPhaseOne (mA) + currentPhaseTwoHQ (mA)
  resolveEnergyPerVideoSeconds(rule: dsDtos.CameraScheduleRule): number {
    const id = rule?.cameraFormatId;
    const fmts = this.cameraFormatsSig();
    const fallback_mA = 315;
    if (!id || !fmts || !fmts.length) return fallback_mA;
    const fmt = fmts.find((f) => f?.formatId === id);
    const c1 = fmt?.currentPhaseOne;
    const c2 = fmt?.currentPhaseTwoHQ;
    const sum =
      typeof c1 === 'number' &&
      isFinite(c1) &&
      c1 >= 0 &&
      typeof c2 === 'number' &&
      isFinite(c2) &&
      c2 >= 0
        ? c1 + c2
        : undefined;
    return sum ?? fallback_mA;
  }

  getLifetimeVideo(rule: dsDtos.CameraScheduleRule): CameraLifetime {
    const { onTime, cycleTime, startTime, mainTriggerValue, cameraScheduleRuleMainTrigger, size } =
      rule || ({} as dsDtos.CameraScheduleRule);
    if (
      !startTime ||
      !onTime ||
      !cycleTime ||
      !mainTriggerValue ||
      cameraScheduleRuleMainTrigger !== 'TIMER'
    ) {
      return undefined;
    }
    const secsInDay = 24 * 60 * 60;
    const current_mA = this.resolveEnergyPerVideoSeconds(rule); // current, not energy
    const batteryCapacity = this.cameraBatteryCapacityAh();
    const sdCapacity = 256 * 1024; // MiB (256 GB)
    const bitRate = this.resolveVideoBitrateBps(rule) || 0;
    const onTimeDay = onTime / (1440 * 60);
    const cycleTimeDay = cycleTime / (1440 * 60);
    const timeIntervalDay = mainTriggerValue / 1440;
    const videoLengthDay = (size || 0) / secsInDay;
    const videoLengthHours = (size || 0) / 3600; // seconds -> hours
    const energyPerVideo_mAh = current_mA * videoLengthHours; // mAh per video

    const batteryDays = Math.floor(
      (batteryCapacity * timeIntervalDay * cycleTimeDay * 1000) / (onTimeDay * energyPerVideo_mAh),
    );
    const sdDays = Math.floor(
      (sdCapacity * 1024 * 1024) /
        (((onTimeDay / timeIntervalDay) * bitRate * (videoLengthDay / 8)) / cycleTimeDay) /
        secsInDay,
    );
    const batteryCount = Math.floor((batteryCapacity * 1000) / energyPerVideo_mAh);
    const sdCount = Math.floor(
      (sdCapacity * 1024 * 1024) / ((bitRate / 8) * videoLengthDay * secsInDay),
    );
    const _startTime = utcMoment(startTime).isBefore(utcMoment())
      ? utcMoment()
      : utcMoment(startTime);
    return {
      date: _startTime.add({ day: Math.min(batteryDays, sdDays) }).toDate(),
      batteryDays,
      sdDays,
      batteryCount,
      sdCount,
      days: Math.min(batteryDays, sdDays),
      count: Math.min(batteryCount, sdCount),
    };
  }

  getLifetimePhoto(rule: dsDtos.CameraScheduleRule): CameraLifetime {
    if (
      !rule.startTime ||
      !rule.onTime ||
      !rule.cycleTime ||
      !rule.mainTriggerValue ||
      rule.cameraScheduleRuleMainTrigger !== 'TIMER'
    ) {
      return undefined;
    }
    const batteryCapacity = this.cameraBatteryCapacityAh();
    const energyPerPhoto = 0.797777778; // mAh/photo
    const sdCapacity = 256 * 1024; // MiB (256 GB)
    const storage = this.resolvePhotoStorageBytes(rule);
    const onTimeDay = rule.onTime / (1440 * 60);
    const timeIntervalDay = rule.mainTriggerValue / 1440;
    const cycleTimeDay = rule.cycleTime / (1440 * 60);

    const batteryDays = Math.floor(
      (batteryCapacity * 1000) / (((onTimeDay / timeIntervalDay) * energyPerPhoto) / cycleTimeDay),
    );
    const sdDays = Math.floor(
      (sdCapacity * 1024 * 1024) / (((onTimeDay / timeIntervalDay) * storage) / cycleTimeDay),
    );
    const batteryCount = Math.floor((batteryCapacity * 1000) / energyPerPhoto);
    const sdCount = Math.floor((sdCapacity * 1024 * 1024) / storage);
    const startTime = utcMoment(rule.startTime).isBefore(utcMoment())
      ? utcMoment()
      : utcMoment(rule.startTime);
    return {
      date: startTime.add({ day: Math.min(batteryDays, sdDays) }).toDate(),
      batteryDays,
      sdDays,
      batteryCount,
      sdCount,
      days: Math.min(batteryDays, sdDays),
      count: Math.min(batteryCount, sdCount),
    };
  }

  // A rule is only usable in the multi-rule (getLifetimeForRules) simulation if its trigger
  private isSupportedLifetimeRule(rule: dsDtos.CameraScheduleRule): boolean {
    if (!rule || !rule.startTime || !rule.onTime || !rule.cycleTime) return false;
    if (rule.cameraScheduleRuleMainTrigger === 'TIMER') return Boolean(rule.mainTriggerValue);
    return rule.cameraScheduleRuleMainTrigger === 'GPS';
  }

  // Number of camera fixes on the given day for a single rule, from whichever trigger it uses.
  private schedulesPerDay(
    rule: dsDtos.CameraScheduleRule,
    dayMoment: Moment,
    gpsFixesMillis: number[] | undefined,
  ): number {
    if (rule.cameraScheduleRuleMainTrigger === 'GPS') {
      return this.cameraScheduleService.numberOfSchedulesFromGpsFixesInDay(
        rule,
        dayMoment,
        gpsFixesMillis || [],
      );
    }
    return this.cameraScheduleService.numberOfSchedulesInDay(rule, dayMoment);
  }

  // All GPS fixes (deduplicated across every configured GPS schedule rule) on the given day.
  private gpsFixesMillisInDay(
    date: Moment,
    gpsRulesObj: { [key: number]: webDtos.ScheduleRule },
    gpsScheduleConfig: ScheduleTypeConfig,
  ): number[] {
    const { start, end } = this.calendarCalculationService.getRange(date, 'day');
    return this.gpsFixesMillisInRange(start, end, gpsRulesObj, gpsScheduleConfig);
  }

  private gpsFixesMillisInRange(
    from: Moment,
    to: Moment,
    gpsRulesObj: { [key: number]: webDtos.ScheduleRule },
    gpsScheduleConfig: ScheduleTypeConfig,
  ): number[] {
    const fixesMillis = new Set<number>();
    for (const [index, gpsRule] of Object.entries(gpsRulesObj || {})) {
      this.calendarCalculationService
        .schedulesInRange(gpsRule, from, to, Number(index), gpsScheduleConfig)
        .forEach((fix) => fixesMillis.add(fix.millis));
    }
    return Array.from(fixesMillis).sort((a, b) => a - b);
  }

  // calculate lifetime for multiple rules (sums per-day battery and storage usage across rules)
  getLifetimeForRules(
    rulesObj: { [key: number]: dsDtos.CameraScheduleRule },
    startDate: Moment,
    gpsRulesObj?: { [key: number]: webDtos.ScheduleRule },
    gpsScheduleConfig?: ScheduleTypeConfig,
  ): CameraLifetime {
    if (!rulesObj) return undefined;

    const rules = Object.values(rulesObj || {});
    if (!rules.length) return undefined;

    // Constants aligned with single-rule formulas
    const batteryCapacity = this.cameraBatteryCapacityAh(); // Ah
    const totalBattery_mAh = batteryCapacity * 1000;
    const sdCapacityMiB = 256 * 1024; // MiB (256 GB)
    const totalSd_bytes = sdCapacityMiB * 1024 * 1024;
    const energyPerPhoto_mAh = 0.797777778;

    const validRules = this.filterSupportedRules(rules);

    if (!validRules.length) return undefined;

    const hasGpsTriggerRule = validRules.some(
      (rule) => rule.cameraScheduleRuleMainTrigger === 'GPS',
    );
    const canResolveGpsFixes =
      hasGpsTriggerRule && Boolean(gpsRulesObj) && Boolean(gpsScheduleConfig);

    // camera only triggers on every (skipCount + 1)-th in-window GPS fix.
    const gpsFixOffsetByRule = this.collectGpsFixOffsets(
      validRules,
      startDate,
      canResolveGpsFixes,
      gpsRulesObj,
      gpsScheduleConfig,
    );

    const { batteryDays, sdDays } = this.simulateLifetimeDays(
      validRules,
      startDate,
      canResolveGpsFixes,
      gpsRulesObj,
      gpsScheduleConfig,
      gpsFixOffsetByRule,
      totalBattery_mAh,
      totalSd_bytes,
      energyPerPhoto_mAh,
    );

    const days = Math.min(batteryDays, sdDays);

    return {
      date: startDate
        .clone()
        .add({ day: Number.isFinite(days) ? days : 0 })
        .toDate(),
      batteryDays: Number.isFinite(batteryDays) ? batteryDays : 0,
      sdDays: Number.isFinite(sdDays) ? sdDays : 0,
    };
  }

  private filterSupportedRules(rules: dsDtos.CameraScheduleRule[]): dsDtos.CameraScheduleRule[] {
    const validRules: dsDtos.CameraScheduleRule[] = [];

    for (const rule of rules) {
      if (!this.isSupportedLifetimeRule(rule)) continue;
      validRules.push(rule);
    }

    return validRules;
  }

  private collectGpsFixOffsets(
    validRules: dsDtos.CameraScheduleRule[],
    startDate: Moment,
    canResolveGpsFixes: boolean,
    gpsRulesObj?: { [key: number]: webDtos.ScheduleRule },
    gpsScheduleConfig?: ScheduleTypeConfig,
  ): Map<dsDtos.CameraScheduleRule, number> {
    const gpsFixOffsetByRule = new Map<dsDtos.CameraScheduleRule, number>();
    if (canResolveGpsFixes) {
      for (const rule of validRules) {
        if (rule.cameraScheduleRuleMainTrigger !== 'GPS' || !rule.startTime) continue;
        const ruleStart = utcMoment(rule.startTime, true);
        if (!startDate.isAfter(ruleStart)) {
          gpsFixOffsetByRule.set(rule, 0);
          continue;
        }
        const preFixes = this.gpsFixesMillisInRange(
          ruleStart,
          startDate,
          gpsRulesObj,
          gpsScheduleConfig,
        );
        const inWindowPre = this.cameraScheduleService.schedulesFromGpsFixesInRange(
          rule,
          ruleStart,
          startDate,
          preFixes,
        );
        gpsFixOffsetByRule.set(rule, inWindowPre.length);
      }
    }
    return gpsFixOffsetByRule;
  }

  private simulateLifetimeDays(
    validRules: dsDtos.CameraScheduleRule[],
    startDate: Moment,
    canResolveGpsFixes: boolean,
    gpsRulesObj?: { [key: number]: webDtos.ScheduleRule },
    gpsScheduleConfig?: ScheduleTypeConfig,
    gpsFixOffsetByRule?: Map<dsDtos.CameraScheduleRule, number>,
    totalBattery_mAh?: number,
    totalSd_bytes?: number,
    energyPerPhoto_mAh?: number,
  ): { batteryDays: number; sdDays: number } {
    let cumulativeBattery_mAh = 0;
    let cumulativeStorage_bytes = 0;

    let batteryDays = 0;
    let sdDays = 0;

    const maxDaysSimulate = 10957; // safety cap ~30 years

    for (let day = 0; day < maxDaysSimulate; day++) {
      const dayMoment = startDate.clone().add({ day });

      // Only computed once per day (not per rule) since it is shared by every GPS-triggered rule.
      const gpsFixesMillis = canResolveGpsFixes
        ? this.gpsFixesMillisInDay(dayMoment, gpsRulesObj, gpsScheduleConfig)
        : undefined;

      const { dailyBattery_mAh, dailyStorage_bytes } = this.calculateDailyUsage(
        validRules,
        dayMoment,
        canResolveGpsFixes,
        gpsRulesObj,
        gpsScheduleConfig,
        gpsFixesMillis,
        gpsFixOffsetByRule,
        energyPerPhoto_mAh,
      );

      // update and day counters
      cumulativeBattery_mAh += dailyBattery_mAh;
      cumulativeStorage_bytes += dailyStorage_bytes;

      const batteryOk = cumulativeBattery_mAh <= totalBattery_mAh;
      const sdOk = cumulativeStorage_bytes <= totalSd_bytes;

      if (batteryOk) batteryDays++;
      if (sdOk) sdDays++;

      if (!batteryOk && !sdOk) break;
    }

    return { batteryDays, sdDays };
  }

  private calculateDailyUsage(
    validRules: dsDtos.CameraScheduleRule[],
    dayMoment: Moment,
    canResolveGpsFixes: boolean,
    gpsRulesObj?: { [key: number]: webDtos.ScheduleRule },
    gpsScheduleConfig?: ScheduleTypeConfig,
    gpsFixesMillis?: number[],
    gpsFixOffsetByRule?: Map<dsDtos.CameraScheduleRule, number>,
    energyPerPhoto_mAh?: number,
  ): { dailyBattery_mAh: number; dailyStorage_bytes: number } {
    let dailyBattery_mAh = 0;
    let dailyStorage_bytes = 0;

    for (const rule of validRules) {
      let schedulesPerDay: number;

      if (rule.cameraScheduleRuleMainTrigger === 'GPS') {
        schedulesPerDay = this.countGpsSchedulesForDay(
          rule,
          dayMoment,
          canResolveGpsFixes,
          gpsFixesMillis,
          gpsFixOffsetByRule,
        );
      } else {
        schedulesPerDay = this.schedulesPerDay(rule, dayMoment, gpsFixesMillis);
      }

      if (!schedulesPerDay) continue;

      if (rule.cameraScheduleRuleType === 'VIDEO') {
        const seconds = (schedulesPerDay || 0) * (rule.size || 0);
        const hours = seconds / 3600;
        const current_mA_rule = this.resolveEnergyPerVideoSeconds(rule);
        const bitRate_bps_rule = this.resolveVideoBitrateBps(rule) || 0;
        dailyBattery_mAh += current_mA_rule * hours;
        dailyStorage_bytes += (bitRate_bps_rule / 8) * seconds;
      } else if (rule.cameraScheduleRuleType === 'PICTURE') {
        const photosPerEvent = Math.max(1, rule.size || 1);
        const photos = (schedulesPerDay || 0) * photosPerEvent;
        const photoStorage_bytes_rule = this.resolvePhotoStorageBytes(rule);
        dailyBattery_mAh += photos * energyPerPhoto_mAh;
        dailyStorage_bytes += photos * photoStorage_bytes_rule;
      }
    }

    return { dailyBattery_mAh, dailyStorage_bytes };
  }

  private countGpsSchedulesForDay(
    rule: dsDtos.CameraScheduleRule,
    dayMoment: Moment,
    canResolveGpsFixes: boolean,
    gpsFixesMillis?: number[],
    gpsFixOffsetByRule?: Map<dsDtos.CameraScheduleRule, number>,
  ): number {
    let schedulesPerDay: number;

    // Apply the camera GPS skip count (mainTriggerValue)
    const inWindow = canResolveGpsFixes
      ? this.cameraScheduleService
          .schedulesFromGpsFixesInDay(rule, dayMoment, gpsFixesMillis || [])
          .slice()
          .sort((a, b) => a.millis - b.millis)
      : [];
    const skipCount = rule.mainTriggerValue || 0;
    if (skipCount && inWindow.length) {
      const offset = gpsFixOffsetByRule.get(rule) || 0;
      schedulesPerDay = inWindow.filter((_s, i) => (offset + i) % (skipCount + 1) === 0).length;
    } else {
      schedulesPerDay = inWindow.length;
    }
    gpsFixOffsetByRule.set(rule, (gpsFixOffsetByRule.get(rule) || 0) + inWindow.length);
    return schedulesPerDay;
  }
}
